import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type DocumentIconType = 'pdf' | 'xls' | 'doc' | 'image' | 'default';

/**
 * Presentational component responsible for everything related to "document type":
 * detecting a file/attachment's type from its name or MIME type, rendering the
 * matching icon, and exposing shared formatting/validation helpers (MIME lookup,
 * allowed-file-type checks, byte/date formatting). Upload, download, and read
 * concerns remain in SelectAttachmentsComponent.
 */
@Component({
  selector: 'app-document-type-icon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './document-type-icon.component.html',
  styleUrl: './document-type-icon.component.css'
})
export class DocumentTypeIconComponent {
  @Input() item: any;

  static readonly MIME_PDF = 'application/pdf';
  static readonly MIME_DOC = 'application/msword';
  static readonly MIME_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  static readonly MIME_XLS = 'application/vnd.ms-excel';
  static readonly MIME_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  static readonly MIME_CSV = 'text/csv';
  static readonly MIME_PNG = 'image/png';
  static readonly MIME_JPEG = 'image/jpeg';
  static readonly MIME_GIF = 'image/gif';
  static readonly MIME_SVG = 'image/svg+xml';
  static readonly MIME_OCTET_STREAM = 'application/octet-stream';
  static readonly MIME_XLSM = 'application/vnd.ms-excel.sheet.macroenabled.12';

  get iconType(): DocumentIconType {
    return DocumentTypeIconComponent.resolveIconType(this.item);
  }

  static resolveIconType(item: any): DocumentIconType {
    if (!item) {
      return 'default';
    }

    let t = '';
    if (typeof item === 'string') {
      t = item.toLowerCase();
    } else if (item.type) {
      t = String(item.type).toLowerCase();
    }

    let name = '';
    if (item?.name) {
      name = String(item.name).toLowerCase();
    } else if (item?.file_name) {
      name = String(item.file_name).toLowerCase();
    }

    // --- Documents ---
    if (t.includes('pdf') || name.endsWith('.pdf')) {
      return 'pdf';
    }

    if (
      t.includes('spreadsheet') ||
      t.includes('excel') ||
      name.endsWith('.xls') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.csv') ||
      name.endsWith('.xlsm')
    ) {
      return 'xls';
    }

    if (
      t.includes('word') ||
      name.endsWith('.doc') ||
      name.endsWith('.docx')
    ) {
      return 'doc';
    }

    // IMAGES: PNG / JPG / JPEG -> ONE icon
    if (
      t.startsWith('image/') ||
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg')
    ) {
      return 'image';
    }

    return 'default';
  }

  /** Convert a file extension to its proper MIME type. */
  static getMimeType(fileName: string): string {
    const ext = (fileName || '').toLowerCase().split('.').pop() || '';
    const mimeTypeMap: { [key: string]: string } = {
      'pdf':  DocumentTypeIconComponent.MIME_PDF,
      'doc':  DocumentTypeIconComponent.MIME_DOC,
      'docx': DocumentTypeIconComponent.MIME_DOCX,
      'xls':  DocumentTypeIconComponent.MIME_XLS,
      'xlsx': DocumentTypeIconComponent.MIME_XLSX,
      'csv':  DocumentTypeIconComponent.MIME_CSV,
      'png':  DocumentTypeIconComponent.MIME_PNG,
      'jpg':  DocumentTypeIconComponent.MIME_JPEG,
      'jpeg': DocumentTypeIconComponent.MIME_JPEG,
      'gif':  DocumentTypeIconComponent.MIME_GIF,
      'svg':  DocumentTypeIconComponent.MIME_SVG,
      'xlsm': DocumentTypeIconComponent.MIME_XLSM,
    };
    return mimeTypeMap[ext] || DocumentTypeIconComponent.MIME_OCTET_STREAM;
  }

  /** Whether the given file matches one of the upload-allowed MIME types/extensions. */
  static isAllowedFileType(file: File): boolean {
    const allowedMimes = new Set<string>([
      DocumentTypeIconComponent.MIME_PDF,
      DocumentTypeIconComponent.MIME_DOCX, // .docx
      DocumentTypeIconComponent.MIME_XLSX, // .xlsx
      DocumentTypeIconComponent.MIME_CSV,
      DocumentTypeIconComponent.MIME_PNG,
      DocumentTypeIconComponent.MIME_JPEG,
      DocumentTypeIconComponent.MIME_XLSM,
    ]);

    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    if (type && allowedMimes.has(type)) {
      return true;
    }

    // Fallback by extension when browser doesn't set type reliably
    return (
      name.endsWith('.pdf') ||
      name.endsWith('.docx') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.csv') ||
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.xlsm')
    );
  }

  static formatBytes(bytes: number): string {
    if (!bytes) {
      return '0 B';
    }
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Number.parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
  }

  static formatDate(iso: string): string {
    const d = new Date(iso);
    // Format like: "Nov 10, 2024, 2:20 PM"
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  static getSizeFromBase64(b64: string): number {
    const padding = DocumentTypeIconComponent.getBase64Padding(b64);
    return (b64.length * 3) / 4 - padding;
  }

  private static getBase64Padding(b64: string): number {
    if (b64.endsWith('==')) { return 2; }
    if (b64.endsWith('=')) { return 1; }
    return 0;
  }
}
